import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Divider,
  Empty,
  Segmented,
  Select,
  Spin,
  Typography,
} from 'antd';

import { fetchSyntheticBondCharts, selectors } from 'state';
import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';
import BondChartCard, { BondChartMode } from './BondChart';
import IssuedBondPriceChart, {
  IssuedBondPricePoint,
} from './IssuedBondPriceChart';
import {
  InfoLabel,
  InfoPoint,
  ModelOverview,
  PricingModelExplanation,
} from './ModelGuide';
import './styles.css';

const MATURITY_OPTIONS = [2, 3, 5, 7, 10].map(years => ({
  label: `${years} years`,
  value: years,
}));

type IssuedBond = {
  issue_date: string;
  issue_timestamp: string;
  maturity_years: number;
  maturity_timestamp: string;
  face_value: number;
  coupon_frequency: number;
  coupon: number;
  minimum_priced_maturity_years: number;
  reference_asset: {
    id: number;
    name: string;
    ric: string | null;
  };
  reference_timestamp: string;
  price_series: IssuedBondPricePoint[];
};

type IssuedBondResponse = {
  issued_bond: IssuedBond;
};

const responseErrorMessage = (result: unknown): string => {
  if (typeof result === 'string') return result;
  if (result && typeof result === 'object') {
    const messages = Object.values(result as Record<string, unknown>).flatMap(
      value => (Array.isArray(value) ? value : [value]),
    );
    if (messages.length) return messages.map(String).join(' ');
  }
  return 'The bond could not be issued.';
};

const BondsPlayground: React.FC = () => {
  const dispatch = useDispatch();
  const bonds = useSelector(selectors.syntheticBondCharts);
  const fetched = useSelector(selectors.syntheticBondChartsFetched);
  const fetching = useSelector(selectors.syntheticBondChartsFetching);
  const error = useSelector(selectors.syntheticBondChartsError);
  const [chartMode, setChartMode] = useState<BondChartMode>('price');
  const [issueDate, setIssueDate] = useState<Dayjs | null>(dayjs('2021-08-23'));
  const [maturity, setMaturity] = useState<number>(10);
  const [issuedBond, setIssuedBond] = useState<IssuedBond>();
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState<string>();

  useEffect(() => {
    if (!fetched && !fetching && !error) {
      dispatch(fetchSyntheticBondCharts({}));
    }
  }, [dispatch, error, fetched, fetching]);

  const refresh = () => dispatch(fetchSyntheticBondCharts({}));

  const issueBond = async () => {
    if (!issueDate || maturity === undefined) return;

    setIssuing(true);
    setIssueError(undefined);
    setIssuedBond(undefined);

    try {
      const { result, status } = await apiCaller<{
        issue_date: string;
        maturity: number;
      }>({
        path: '/synthetic_bond_playground/issued_bond_details',
        method: 'GET',
        data: {
          issue_date: issueDate.format('YYYY-MM-DD'),
          maturity,
        },
        apiUrl: API_URL,
      });

      if (status.toString()[0] !== '2') {
        throw new Error(responseErrorMessage(result));
      }

      setIssuedBond((result as IssuedBondResponse).issued_bond);
    } catch (requestError) {
      setIssueError(
        requestError instanceof Error
          ? requestError.message
          : 'The bond could not be issued.',
      );
    } finally {
      setIssuing(false);
    }
  };

  return (
    <div className="bonds-playground">
      <main className="bonds-playground__workspace">
        <Card title="Synthetic Bond Calculation Playground">
          <ModelOverview />

          <Divider />

          <section>
            <div className="bonds-playground__section-heading">
              <div>
                <Typography.Title level={5}>Issue a test bond</Typography.Title>
                <Typography.Paragraph type="secondary">
                  Select the bond's fixed terms. Changing either input clears
                  the previous calculation.
                </Typography.Paragraph>
              </div>
              <InfoPoint title="Issue-time lookup">
                The selected maturity maps to its matching benchmark asset. The
                last complete bid/ask yield row observed on the chosen calendar
                date fixes the precise issue timestamp; its ask yield becomes
                the annual coupon rate.
              </InfoPoint>
            </div>

            <div className="bonds-playground__constructor">
              <div className="bonds-playground__field">
                <Typography.Text strong>
                  <InfoLabel label="Issue date" title="Issue date">
                    The latest complete bid/ask yield row on this date must be
                    non-crossed for the benchmark matching the selected initial
                    maturity.
                  </InfoLabel>
                </Typography.Text>
                <DatePicker
                  disabled={issuing}
                  format="YYYY-MM-DD"
                  value={issueDate}
                  onChange={value => {
                    setIssueDate(value);
                    setIssuedBond(undefined);
                    setIssueError(undefined);
                  }}
                />
              </div>

              <div className="bonds-playground__field">
                <Typography.Text strong>
                  <InfoLabel label="Initial maturity" title="Initial maturity">
                    The available choices are the five maintained benchmark
                    tenors. This choice fixes the maturity date and the initial
                    benchmark used to set the coupon.
                  </InfoLabel>
                </Typography.Text>
                <Select<number>
                  disabled={issuing}
                  options={MATURITY_OPTIONS}
                  placeholder="Select maturity"
                  value={maturity}
                  onChange={value => {
                    setMaturity(value);
                    setIssuedBond(undefined);
                    setIssueError(undefined);
                  }}
                />
              </div>

              <Button
                className="bonds-playground__issue-button"
                disabled={!issueDate || maturity === undefined}
                loading={issuing}
                onClick={issueBond}
                type="primary"
              >
                Issue bond
              </Button>
            </div>
          </section>

          {issueError && (
            <Alert
              className="bonds-playground__issue-result"
              message="Bond could not be issued"
              description={issueError}
              showIcon
              type="error"
            />
          )}

          {issuedBond && (
            <Card
              className="bonds-playground__issue-result"
              size="small"
              title="Issued bond details"
            >
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Issue date">
                  {issuedBond.issue_date}
                </Descriptions.Item>
                <Descriptions.Item label="Initial maturity">
                  {issuedBond.maturity_years} years
                </Descriptions.Item>
                <Descriptions.Item label="Maturity timestamp">
                  {issuedBond.maturity_timestamp}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <InfoLabel label="Fixed coupon" title="Fixed coupon">
                      This is the issuing benchmark's ask yield at issuance. It
                      remains unchanged for the life of the synthetic bond.
                    </InfoLabel>
                  }
                >
                  <Typography.Text strong>
                    {issuedBond.coupon.toLocaleString(undefined, {
                      maximumFractionDigits: 6,
                    })}
                    %
                  </Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <InfoLabel label="Reference bond" title="Reference bond">
                      The maintained benchmark selected by the bond's initial
                      maturity. It sets the coupon only; later valuations use
                      the full interpolated curve.
                    </InfoLabel>
                  }
                >
                  {issuedBond.reference_asset.name}
                  {issuedBond.reference_asset.ric
                    ? ` · ${issuedBond.reference_asset.ric}`
                    : ''}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <InfoLabel
                      label="Yield observation"
                      title="Issuance observation"
                    >
                      The exact paired market-history timestamp whose ask yield
                      sets the fixed coupon and whose two sides seed valuation.
                    </InfoLabel>
                  }
                >
                  {issuedBond.reference_timestamp}
                </Descriptions.Item>
                <Descriptions.Item label="Face value">
                  {issuedBond.face_value.toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Coupon schedule">
                  Semiannual ({issuedBond.coupon_frequency} payments/year)
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <InfoLabel
                      label="Price convention"
                      title="Price convention"
                    >
                      Each yield side first produces a dirty present value. The
                      same accrued interest is removed from both values before
                      PM receives the clean bid and ask quotes.
                    </InfoLabel>
                  }
                >
                  Clean bid/ask charted · dirty settlement values available on
                  hover
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <InfoLabel label="Price series" title="Price series">
                      A snapshot is produced at issuance, whenever a maintained
                      node receives a valid paired update, and at every coupon
                      boundary within the available system-data horizon. The
                      series never projects beyond the final DAILY observation.
                    </InfoLabel>
                  }
                >
                  {issuedBond.price_series.length.toLocaleString()} valuation
                  points
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <InfoLabel label="Series cutoff" title="Series cutoff">
                      V1 does not extrapolate beneath the shortest maintained
                      yield tenor, so calculations stop below this residual
                      maturity.
                    </InfoLabel>
                  }
                >
                  {issuedBond.minimum_priced_maturity_years} years residual
                  maturity
                </Descriptions.Item>
              </Descriptions>

              <div className="bonds-playground__fixed-moving">
                <div>
                  <Typography.Text strong>Fixed after issuance</Typography.Text>
                  <Typography.Paragraph>
                    Face value, coupon rate, maturity, and all semiannual
                    cash-flow dates.
                  </Typography.Paragraph>
                </div>
                <div>
                  <Typography.Text strong>
                    Recomputed through time
                  </Typography.Text>
                  <Typography.Paragraph>
                    Residual maturity, active bid/ask curve nodes, interpolated
                    yields, accrued interest, and clean/dirty prices.
                  </Typography.Paragraph>
                </div>
              </div>

              <PricingModelExplanation />
              <IssuedBondPriceChart points={issuedBond.price_series} />
            </Card>
          )}
        </Card>
      </main>

      <aside className="bonds-playground__sidecar">
        <div className="bonds-playground__sidecar-header">
          <div>
            <div className="bonds-playground__sidecar-title">
              <Typography.Title level={4}>Bond market data</Typography.Title>
              <InfoPoint title="Reference market data">
                These are the raw maintained benchmark histories. The toggle is
                for inspection only: issuance uses ask yield to fix the coupon;
                repricing then interpolates both bid and ask yields. Displayed
                source prices are not used by the synthetic calculator.
              </InfoPoint>
            </div>
            <Typography.Text type="secondary">
              {bonds.length} bond{bonds.length === 1 ? '' : 's'}
            </Typography.Text>
          </div>
          <Segmented
            options={[
              { label: 'Prices', value: 'price' },
              { label: 'Yields', value: 'yield' },
            ]}
            value={chartMode}
            onChange={value => setChartMode(value as BondChartMode)}
          />
        </div>

        {error && (
          <Alert
            action={
              <Button size="small" onClick={refresh}>
                Retry
              </Button>
            }
            message="Could not load bond chart data"
            description={error}
            showIcon
            type="error"
          />
        )}

        <Spin spinning={fetching}>
          <div className="bonds-playground__charts">
            {!fetching && !error && bonds.length === 0 ? (
              <Empty description="No playground bonds were found." />
            ) : (
              bonds.map(bond => (
                <BondChartCard key={bond.id} bond={bond} mode={chartMode} />
              ))
            )}
          </div>
        </Spin>
      </aside>
    </div>
  );
};

export default BondsPlayground;
